import { BillingUsers } from "@mcleod/ar/src/BillingUsers";
import { Button, ClickEvent, DataSource, DataSourceMode, DataSourceModeChangeEvent, Layout, SlideoutDecorator, Table } from "@mcleod/components";
import { Model } from "@mcleod/core";
import { MovementUsers } from "@mcleod/dispatch/src/MovementUsers";
import { getPayrollControlBoolean } from "./models/ModelPayrollControl";
import { ResponsibleHist } from "./ResponsibleHist";

/**
 * The purpose of this class is to display the provided layout in a SlideoutDecorator.  The provided layout is expected
 * to be a 'users info' layout from a transaction screen.  For example, this would be the OrderUsers layout from the Orders
 * screen.  The important thing about the provided layout is that it is expected to contain an instance of
 * ResponsibleHist as a nested layout.
 */
export class UsersResponsibilitySlideoutTrans {
    public layout: Layout;
    private layoutName: string;
    private title: string;
    private idFilter: any;
    private sharedFieldNames: string[];
    private responsibleHist: ResponsibleHist;
    private layoutRespsonsibleHistDataSource: DataSource;
    private parentMainDataSource: DataSource;
    private parentRespsonsibleHistDataSource: DataSource;
    private isCommissionsEnabled: boolean;
    private enableCustomerCommissions: boolean;
    public includeCommissions: boolean = false;

    public constructor(layoutName: string, title: string, idFilter: any, sharedFieldNames: string[], parentMainDataSource: DataSource, parentRespsonsibleHistDataSource: DataSource, isBillingCreditRecord: boolean = false) {
        this.title = title;
        this.idFilter = idFilter;
        this.sharedFieldNames = sharedFieldNames;
        this.parentMainDataSource = parentMainDataSource;
        this.parentRespsonsibleHistDataSource = parentRespsonsibleHistDataSource;
        this.layout = Layout.getLayout(layoutName);
        this.layoutName = layoutName;
        if (isBillingCreditRecord && this.layout instanceof BillingUsers) {
            this.layout.isCreditBill = isBillingCreditRecord;
        }
        this.layout.addLayoutLoadListener(async event => {
            this.layout.mainDataSource.addAfterModeChangeListener((event: DataSourceModeChangeEvent) => {
                if (event.newMode === DataSourceMode.ADD) {
                    for (const field of this.sharedFieldNames) {
                        this.layout.mainDataSource.activeRow.set(field, this.parentMainDataSource.activeRow.get(field));
                        if (this.parentMainDataSource.activeRow.hasLookupModelData(field))
                            this.layout.mainDataSource.activeRow.addLookupModelData(field, this.parentMainDataSource.activeRow.getFirstLookupModelData(field));
                    }
                    this.layout.mainDataSource.displayDataInBoundComponents();
                }
            });
            this.responsibleHist = this.findResponsibleHistNestedLayout(this.layout);
            if (this.responsibleHist != null) {
                this.layoutRespsonsibleHistDataSource = this.findResponsibleHistDataSource(this.layout);
                await this.responsibleHist.setup(this.layoutRespsonsibleHistDataSource/*, this.layout.mainDataSource*/);
            }

            this.isCommissionsEnabled = getPayrollControlBoolean("enable_commissions", false);

            this.responsibleHist.orderIds = this.parentMainDataSource.activeRow.get("order_ids", null);
            const isManifested = this.parentMainDataSource.activeRow.get("manifest_id", null) !== null;

            if (!isManifested || !this.isCommissionsEnabled || this.layoutName != "lme/dispatch/MovementUsers") {
                this.responsibleHist.tableResponsibleHist.removeColumn("Order ID");
            }

            return Model.searchSingleRecord("lme/dispatch/agent-access-ctrl").then(model => {
                this.enableCustomerCommissions = model?.getBoolean("auto_assign_cus_commissions");
                if (!(this.isCommissionsEnabled && (this.enableCustomerCommissions || this.includeCommissions))) {
                    this.responsibleHist.tableResponsibleHist.removeColumn("Commissions Role Type");
                    this.responsibleHist.tableResponsibleHist.removeColumn("Commissions Eligible");
                    this.responsibleHist.tableResponsibleHist.removeColumn("Split %");
                }
            });
        });
    }

    public show(enableButton: boolean = true) {
        const saveButton = new Button({
            id: "ursSaveButton",
            caption: "Save",
            backgroundColor: "primary",
            color: "primary.reverse",
            borderWidth: 0,
            rowBreak: false,
            enabled: enableButton,
            onClick: (event: ClickEvent) => {
                this.processSaveClick(event.target as Button, sod);
            }
        });
        const sod = new SlideoutDecorator({
            layout: this.layout,
            width: "70%",
            maxWidth: 1320,
            fillVerticalSpace: true,
            title: this.title,
            displayOverlay: true,
            overlayProps: { closeOnClickOff: false, greyedBackground: false },
            addlComponents: [saveButton],
            onClose: () => {
                sod.slideOut();
            },
            layoutLoadListeners: () => {
                const movementUsers = this.layout as MovementUsers;
                const tableResponsibleHist: Table = movementUsers.layoutResponsibleHist.tableResponsibleHist;
                saveButton.visible = tableResponsibleHist.allowDelete || tableResponsibleHist.allowEdit;
            },
            doAfterSlideIn: (sod: SlideoutDecorator) => {
                this.loadData();
            }
        });
    }

    public loadData() {
        if (this.parentMainDataSource.mode === DataSourceMode.UPDATE) {
            if (this.parentMainDataSource.url == this.layout.mainDataSource.url) {
                this.layout.mainDataSource.setRowsAndMode(this.parentMainDataSource.mode, this.parentMainDataSource.data);
            } else {
                this.layout.mainDataSource.search({ ...this.idFilter });
            }
        } else if (this.parentMainDataSource.mode === DataSourceMode.ADD) {
            if (this.layoutRespsonsibleHistDataSource != null) {
                this.layoutRespsonsibleHistDataSource.data = this.parentRespsonsibleHistDataSource.getDataCopy();
                this.layoutRespsonsibleHistDataSource.displayDataInBoundComponents();
                if (this.parentMainDataSource.activeRow.get("rf_entry_code_required", false) === true)
                    this.responsibleHist.addBlankEntryRecordToTable();
            }
        }
        this.layout.mainDataSource.mode = this.parentMainDataSource.mode;
        if (this.layoutRespsonsibleHistDataSource != null)
            this.layoutRespsonsibleHistDataSource.mode = this.parentMainDataSource.mode;

        this.responsibleHist.commissionsEnabled = (this.isCommissionsEnabled && this.enableCustomerCommissions);

        if (this.layoutName === "lme/dispatch/MovementUsers") {
            this.responsibleHist.commissionRoleType = "D";
        } else if (this.layoutName === "lme/dispatch/OrderUsers") {
            this.responsibleHist.commissionRoleType = "E";
        }
    }

    private async processSaveClick(button: Button, sod: SlideoutDecorator) {
        try {
            button.busy = true;
            if (this.parentMainDataSource.isAddingOrUpdating() === true) {
                if (sod.validateSimple() !== true)
                    return;
                if (this.parentMainDataSource.mode === DataSourceMode.UPDATE)
                    await this.layout.mainDataSource.post();

                for (const field of this.sharedFieldNames) {
                    const parentActiveRow = this.parentMainDataSource.activeRow;
                    parentActiveRow.setLookupModelData(field, this.layout.mainDataSource.activeRow.getLookupModelData(field));
                    parentActiveRow.set(field, this.layout.mainDataSource.activeRow.get(field));
                    parentActiveRow.resetOriginalData(field);
                }
                this.parentMainDataSource.displayDataInBoundComponents();

                if (this.responsibleHist != null) {
                    this.parentRespsonsibleHistDataSource.data = this.layoutRespsonsibleHistDataSource.data;
                }
                sod.slideOut();
            } else if (this.parentMainDataSource.mode === DataSourceMode.SEARCH) {
                if (this.responsibleHist != null) {
                    this.parentRespsonsibleHistDataSource.data = this.layoutRespsonsibleHistDataSource.data;
                }
                sod.slideOut();
            }
        } finally {
            button.busy = false;
        }
    }

    private findResponsibleHistNestedLayout(layout: Layout): ResponsibleHist {
        for (const variable of Object.keys(layout)) {
            if (layout[variable] instanceof ResponsibleHist)
                return layout[variable];
        }
        return null; //can return null if AgenciesWithPrimary nested layout was removed due to licensing
    }

    private findResponsibleHistDataSource(layout: Layout): DataSource {
        for (const dataSourceId of layout.dataSourceIDs) {
            if ((layout[dataSourceId] as DataSource).url === "lme/general/responsible-hist")
                return layout[dataSourceId];
        }
        throw new Error("The provided layout does not contain the expected Responsibility Codes DataSource.");
    }
}
